'use client';

import { useContext, useEffect, useRef, useState } from "react";
import './home-mobile.scss';
import LoadingCapabilities from "./LoadingCapabilities";
import Intro from "./Intro";
import Reach from "./Reach";
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import { HOME_SLIDE } from "./slides";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import { ProjectModel } from "@/lib/models/Project";
import { usePathname } from "next/navigation";
import ProjectMobileItem from "./ProjectMobileItem";
import FooterMobile from "@/components/Mobile/FooterMobile";
import { projectPath } from "@/lib/routes";
import { useHoldRouteEntry, useTransitionExit } from "@/components/Mobile/Transition/RouteTransition";
import MobileShellContext from "@/components/Mobile/MobileShellContext";
import Title from "./Title";

const LOADING_BORDER_SETTLE_MS = 450;

type ProgressSlide = HTMLElement & { progress?: number };


interface Props {
  projects: ProjectModel[];
}

const HomeMobile = ({ projects }: Props) => {
  const swiperRef = useRef<SwiperInstance | null>(null);
  const loadingBorderResetTimerRef = useRef<number | null>(null);
  const loadingToTitleDragRef = useRef(false);
  const pathname = usePathname();
  const exit = useTransitionExit();
  const markRouteReady = useHoldRouteEntry(pathname);
  const { slide, setSlide, fadeOutShowreel } = useContext(MobileShellContext);
  const [initialSlide] = useState(slide);
  const [swiperReady, setSwiperReady] = useState(false);
  const [loadingBorderOffset, setLoadingBorderOffset] = useState<number | null>(null);

  const getCurrentProject = () => {
    const projectIndex = slide - HOME_SLIDE.firstProject;
    if (projectIndex < 0 || projectIndex >= projects.length) {
      return undefined;
    }

    return projects[projectIndex];
  }

  // Navigates to project details of current project.
  const goToProjectDetail = (project: ProjectModel) => {
    exit(projectPath(project.slug));
  };

  const changeShowDetail = () => {
    const project = getCurrentProject();
    if (project) goToProjectDetail(project);
  };

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiperReady || !swiper || swiper.realIndex === slide) return;
    swiper.slideToLoop(slide, 0);
  }, [slide, swiperReady]);

  useEffect(() => {
    if (slide !== HOME_SLIDE.title) fadeOutShowreel();
  }, [slide, fadeOutShowreel]);

  useEffect(() => {
    if (swiperReady) markRouteReady(pathname);
  }, [swiperReady, pathname, markRouteReady]);

  const handleNextStep = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const clearLoadingBorderTimer = () => {
    if (loadingBorderResetTimerRef.current !== null) {
      window.clearTimeout(loadingBorderResetTimerRef.current);
      loadingBorderResetTimerRef.current = null;
    }
  };

  const commitSwiperState = (swiper: SwiperInstance) => {
    if (!swiper || swiper.realIndex === slide) return;

    setSlide(swiper.realIndex);
  };

  const resetLoadingBorder = (swiper?: SwiperInstance) => {
    clearLoadingBorderTimer();
    loadingToTitleDragRef.current = false;
    setLoadingBorderOffset(null);

    if (swiper) {
      swiper.allowTouchMove = swiper.realIndex !== HOME_SLIDE.title;
      commitSwiperState(swiper);
    }
  };

  const getLoadingSlideProgress = (swiper: SwiperInstance) => {
    const activeSlide = swiper.slides?.[swiper.activeIndex] as ProgressSlide | undefined;
    if (activeSlide?.getAttribute('data-swiper-slide-index') === String(HOME_SLIDE.loading)) {
      return activeSlide.progress ?? 0;
    }

    const loadingSlides = (Array.from(swiper.slides ?? []) as ProgressSlide[])
      .filter((slide) => slide.getAttribute('data-swiper-slide-index') === String(HOME_SLIDE.loading));

    if (!loadingSlides.length) return 0;

    return loadingSlides.reduce((closest, slide) => {
      return Math.abs(slide.progress ?? 0) < Math.abs(closest.progress ?? 0) ? slide : closest;
    }, loadingSlides[0]).progress ?? 0;
  };

  const syncLoadingBorder = (swiper: SwiperInstance) => {
    if (!loadingToTitleDragRef.current) return;

    const progress = getLoadingSlideProgress(swiper);
    const width = swiper.width || window.innerWidth;
    const offset = Math.max(0, Math.min(width, -progress * width));

    setLoadingBorderOffset(offset);
  };

  const handleSliderMove = (swiper: SwiperInstance) => {
    const diff = swiper.touches?.diff ?? 0;
    const isLoadingToTitleDrag = swiper.realIndex === HOME_SLIDE.loading && diff > 0;

    if (!isLoadingToTitleDrag) {
      return;
    }

    clearLoadingBorderTimer();
    loadingToTitleDragRef.current = true;
    syncLoadingBorder(swiper);
  };

  const handleTouchEnd = (swiper: SwiperInstance) => {
    if (!loadingToTitleDragRef.current) return;

    syncLoadingBorder(swiper);
    clearLoadingBorderTimer();
    loadingBorderResetTimerRef.current = window.setTimeout(() => resetLoadingBorder(swiper), LOADING_BORDER_SETTLE_MS);
  };

  const changeSwiper = (swiper: SwiperInstance) => {
    if (loadingToTitleDragRef.current) return;

    // GX: Added if check to skip unneccessary re-renders
    commitSwiperState(swiper);
  };

  const handlePrevStep = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const isTitleSlide = slide === HOME_SLIDE.title;
  const isProjectSlide = getCurrentProject() !== undefined;

  const renderProjectItem = (item: ProjectModel, index: number) => {
    return (
      <SwiperSlide key={item.id} className="home-content-panel">
        <ProjectMobileItem
          index={index}
          project={item}
          goToDetail={goToProjectDetail}
        />
      </SwiperSlide>
    )
  };

  const renderFooter = () => {
    return (
      <FooterMobile
        isTitle={isTitleSlide}
        project={getCurrentProject()}
        onBack={handlePrevStep}
        onNext={handleNextStep}
        onViewWork={changeShowDetail}
        hideBorder={loadingBorderOffset !== null}
      />
    )
  };

  useEffect(() => {
    return () => clearLoadingBorderTimer();
  }, []);

  return (
    <div className={`home-mobile ${isTitleSlide ? 'is-title-slide' : ''} ${isProjectSlide ? 'is-project-slide' : ''}`}>
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        className='home-mobile-deck'
        initialSlide={initialSlide}
        onSwiper={(swiper: SwiperInstance) => {
          swiperRef.current = swiper;
          swiper.allowTouchMove = swiper.realIndex !== HOME_SLIDE.title;
          setSwiperReady(true);
        }}
        onSlideChange={changeSwiper}
        onProgress={syncLoadingBorder}
        onSliderMove={handleSliderMove}
        onTouchEnd={handleTouchEnd}
        onTransitionEnd={resetLoadingBorder}
        loop
        watchSlidesProgress
      >
        <SwiperSlide>
          <Title
            handleNextStep={handleNextStep}
            isActive={isTitleSlide}
          />
        </SwiperSlide>
        <SwiperSlide className="home-content-panel">
          <LoadingCapabilities />
        </SwiperSlide>
        <SwiperSlide className="home-content-panel">
          <Intro />
        </SwiperSlide>
        {projects.map((item, index) => {
          return renderProjectItem(item, index)
        })}
        <SwiperSlide className="home-content-panel">
          <Reach />
        </SwiperSlide>
      </Swiper>
      {loadingBorderOffset !== null && (
        <div
          className="home-mobile-footer-follow"
          style={{ transform: `translate3d(${loadingBorderOffset}px, 0, 0)` }}
        >
          <div className="footer-mobile-border" />
        </div>
      )}
      {renderFooter()}
    </div>
  )
};

export default HomeMobile;
